import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Search, Upload, FileDown, LayoutGrid, List as ListIcon, GitMerge } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import FacilitiesBoard from "@/components/FacilitiesBoard";
import FacilitiesMerge from "@/components/FacilitiesMerge";
import ImportFacilitiesDialog from "@/components/ImportFacilitiesDialog";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRepFilter } from "@/context/RepFilterContext";
import { useSearchParams } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import HealthScoreChip from "@/components/HealthScoreChip";
import HealthTrend from "@/components/HealthTrend";

const STATUS_TONE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  prospect: "bg-slate-100 text-slate-700 border-slate-200",
  inactive: "bg-slate-50 text-slate-500 border-slate-200",
  on_hold: "bg-orange-50 text-orange-700 border-orange-200",
};

const FL_COUNTIES = ["Miami-Dade","Broward","Palm Beach","Orange","Hillsborough","Duval","Pinellas","Lee","Polk","Seminole","Sarasota","Manatee","Volusia","Brevard","Collier","Alachua","Marion","Leon","Osceola","Escambia","Other"];

function emptyForm() {
  return { name: "", type: "SNF", address: "", city: "", county: "Miami-Dade", zip_code: "", latitude: null, longitude: null, capacity: "", census: "", payer_mix: "", medicaid_id: "", admin_contact_name: "", admin_contact_phone: "", admin_contact_email: "", corporate_owner_id: "none", assigned_rep_id: "none", referral_status: "prospect", notes: "" };
}

export default function Facilities() {
  const { user: me } = useAuth();
  const [urlParams, setUrlParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [health, setHealth] = useState({});
  const [trend, setTrend] = useState({});
  const [owners, setOwners] = useState([]);
  const [reps, setReps] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const { repId: globalRep, setRepId: setGlobalRep, isAdmin } = useRepFilter();
  const [repFilter, setRepFilterLocal] = useState(urlParams.get("rep") || globalRep);
  useEffect(() => { setRepFilterLocal(globalRep); }, [globalRep]);
  const setRepFilter = (v) => { setRepFilterLocal(v); if (isAdmin) setGlobalRep(v); };
  const [assignedToMe, setAssignedToMe] = useState(urlParams.get("mine") === "1");
  const [boardGroup, setBoardGroup] = useState("status");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("list");
  const [importOpen, setImportOpen] = useState(false);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (typeFilter !== "all") params.type = typeFilter;
    if (statusFilter !== "all") params.referral_status = statusFilter;
    if (assignedToMe && me?.user_id) params.assigned_rep_id = me.user_id;
    else if (repFilter !== "all") params.assigned_rep_id = repFilter;
    api.get("/facilities", { params }).then(({ data }) => setRows(data));
    api.get("/facilities/health-scores").then(({ data }) => setHealth(data)).catch(() => {});
    api.get("/facilities/health-history", { params: { days: 30 } }).then(({ data }) => setTrend(data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/owners").then(({ data }) => setOwners(data)); api.get("/users").then(({ data }) => setReps(data)).catch(() => setReps([])); /* eslint-disable-next-line */ }, [q, typeFilter, statusFilter, repFilter, assignedToMe, me?.user_id]);

  const submit = async () => {
    const payload = {
      ...form,
      capacity: form.capacity ? Number(form.capacity) : null,
      census: form.census ? Number(form.census) : null,
      corporate_owner_id: form.corporate_owner_id === "none" ? null : form.corporate_owner_id,
      assigned_rep_id: form.assigned_rep_id === "none" ? null : form.assigned_rep_id,
    };
    try {
      if (editing) await api.put(`/facilities/${editing}`, payload);
      else await api.post("/facilities", payload);
      toast.success(editing ? "Facility updated" : "Facility created");
      setOpen(false); setForm(emptyForm()); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const edit = (r) => {
    setEditing(r.facility_id);
    setForm({ ...emptyForm(), ...r, capacity: r.capacity ?? "", census: r.census ?? "", corporate_owner_id: r.corporate_owner_id || "none", assigned_rep_id: r.assigned_rep_id || "none", zip_code: r.zip_code || "", payer_mix: r.payer_mix || "", medicaid_id: r.medicaid_id || "", admin_contact_name: r.admin_contact_name || "", admin_contact_phone: r.admin_contact_phone || "", admin_contact_email: r.admin_contact_email || "", notes: r.notes || "" });
    setOpen(true);
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="facilities-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Directory</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black">Facilities</h1>
          <p className="text-slate-500 mt-1 text-sm">Florida SNFs and ALFs your team engages with.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyForm()); setEditing(null); } }}>
          <div className="flex gap-2 items-center">
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="facility-import-btn"><Upload className="w-4 h-4 mr-1" /> Import Excel</Button>
            <Button variant="outline" onClick={async () => {
              const token = localStorage.getItem("aceh_token") || "";
              const r = await fetch(`${API}/facilities-bulk/export.xlsx`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
              const blob = await r.blob(); const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url; a.download = "facilities.xlsx"; a.click(); URL.revokeObjectURL(url);
            }} data-testid="facility-export-btn"><FileDown className="w-4 h-4 mr-1" /> Export Excel</Button>
            <DialogTrigger asChild>
              <Button data-testid="facility-new-btn" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" /> New facility</Button>
            </DialogTrigger>
          </div>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit facility" : "New facility"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Name</Label><Input data-testid="fac-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="fac-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SNF">SNF · Skilled Nursing</SelectItem>
                    <SelectItem value="ALF">ALF · Assisted Living</SelectItem>
                    <SelectItem value="IL">IL · Independent Living</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Search address on the web</Label>
                <AddressAutocomplete
                  value={form.address}
                  onChange={(v) => setForm({ ...form, address: v })}
                  onSelect={(r) => setForm({
                    ...form,
                    address: r.street || r.label.split(",")[0],
                    city: r.city || form.city,
                    county: FL_COUNTIES.includes(r.county) ? r.county : (r.county ? "Other" : form.county),
                    zip_code: r.zip_code || form.zip_code,
                    latitude: r.latitude, longitude: r.longitude,
                  })}
                />
                <div className="text-[10px] text-slate-400 font-mono-plex mt-1">Powered by OpenStreetMap · US addresses</div>
              </div>
              <div><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>County</Label>
                <Select value={form.county} onValueChange={v => setForm({ ...form, county: v })}>
                  <SelectTrigger data-testid="fac-county"><SelectValue /></SelectTrigger>
                  <SelectContent>{FL_COUNTIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>ZIP</Label><Input value={form.zip_code} onChange={e => setForm({ ...form, zip_code: e.target.value })} /></div>
              <div><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} /></div>
              <div><Label>Current Census</Label><Input type="number" value={form.census} onChange={e => setForm({ ...form, census: e.target.value })} /></div>
              <div><Label>Payer Mix</Label><Input placeholder="Medicare 40 / Medicaid 40 / Private 20" value={form.payer_mix} onChange={e => setForm({ ...form, payer_mix: e.target.value })} /></div>
              <div><Label>Medicaid ID</Label><Input value={form.medicaid_id} onChange={e => setForm({ ...form, medicaid_id: e.target.value })} /></div>
              <div><Label>Referral Status</Label>
                <Select value={form.referral_status} onValueChange={v => setForm({ ...form, referral_status: v })}>
                  <SelectTrigger data-testid="fac-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospect">Prospect</SelectItem><SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_hold">On hold</SelectItem><SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Corporate Owner</Label>
                <Select value={form.corporate_owner_id} onValueChange={v => setForm({ ...form, corporate_owner_id: v })}>
                  <SelectTrigger data-testid="fac-owner"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {owners.map(o => <SelectItem key={o.owner_id} value={o.owner_id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Assigned Rep</Label>
                <Select value={form.assigned_rep_id} onValueChange={v => setForm({ ...form, assigned_rep_id: v })}>
                  <SelectTrigger data-testid="fac-rep"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Unassigned —</SelectItem>
                    {reps.filter(r => r.active !== false).map(r => (
                      <SelectItem key={r.user_id} value={r.user_id}>{r.name} <span className="text-slate-400 text-xs font-mono-plex ml-1">· {r.role}</span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Admin Contact Name</Label><Input value={form.admin_contact_name} onChange={e => setForm({ ...form, admin_contact_name: e.target.value })} /></div>
              <div><Label>Admin Phone</Label><Input value={form.admin_contact_phone} onChange={e => setForm({ ...form, admin_contact_phone: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Admin Email</Label><Input type="email" value={form.admin_contact_email} onChange={e => setForm({ ...form, admin_contact_email: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="fac-save" className="bg-slate-900 hover:bg-slate-800">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={view} onValueChange={setView} className="mb-4">
        <TabsList>
          <TabsTrigger value="list" data-testid="view-list"><ListIcon className="w-4 h-4 mr-1" /> List</TabsTrigger>
          <TabsTrigger value="board" data-testid="view-board"><LayoutGrid className="w-4 h-4 mr-1" /> Board</TabsTrigger>
          <TabsTrigger value="merge" data-testid="view-merge"><GitMerge className="w-4 h-4 mr-1" /> Merge</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "merge" && <FacilitiesMerge onMerged={load} />}

      <div className="mb-3 flex flex-wrap items-center gap-4" style={{ display: view === "merge" ? "none" : undefined }}>
        <div className="flex items-center gap-2 text-sm">
          <Switch checked={assignedToMe} onCheckedChange={setAssignedToMe} data-testid="toggle-mine" />
          <Label className="cursor-pointer">Assigned to me</Label>
        </div>
        {view === "board" && (
          <div className="flex items-center gap-2 text-sm">
            <Label className="text-xs text-slate-500 font-mono-plex uppercase tracking-widest">Group by</Label>
            <Select value={boardGroup} onValueChange={setBoardGroup}>
              <SelectTrigger className="w-40 h-8" data-testid="board-group"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="status">Status</SelectItem>
                <SelectItem value="rep">Rep (swimlanes)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Card className="p-4 border-slate-200 shadow-none mb-4" style={{ display: view === "merge" ? "none" : undefined }}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="relative md:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="fac-search" placeholder="Search name, city, owner…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="SNF">SNF</SelectItem>
              <SelectItem value="ALF">ALF</SelectItem>
              <SelectItem value="IL">IL</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="prospect">Prospect</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="on_hold">On hold</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={repFilter} onValueChange={setRepFilter}>
            <SelectTrigger data-testid="filter-rep"><SelectValue placeholder="All reps" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All reps</SelectItem>
              {reps.filter(r => r.active !== false).map(r => <SelectItem key={r.user_id} value={r.user_id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-none overflow-hidden" style={{ display: view === "list" ? "block" : "none" }}>
        <table className="w-full text-sm" data-testid="facilities-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest font-mono-plex text-slate-500">
            <tr><th className="p-3">Name</th><th className="p-3">Health</th><th className="p-3">Type</th><th className="p-3">County</th><th className="p-3">Owner</th><th className="p-3">Rep</th><th className="p-3">Census</th><th className="p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-slate-400">No facilities yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.facility_id} className="border-t border-slate-100 row-enter hover:bg-slate-50/60" data-testid={`fac-row-${r.facility_id}`}>
                <td className="p-3 font-medium text-slate-900">
                  <Link to={`/facilities/${r.facility_id}`} className="hover:underline">{r.name}</Link>
                  <div className="text-xs text-slate-500">{r.city}</div>
                </td>
                <td className="p-3"><div className="flex flex-col gap-1 items-start"><HealthScoreChip score={health[r.facility_id]} /><HealthTrend series={trend[r.facility_id]} width={64} height={16} /></div></td>
                <td className="p-3"><Badge variant="outline">{r.type}</Badge></td>
                <td className="p-3 text-slate-700">{r.county}</td>
                <td className="p-3 text-slate-700 text-xs">{r.corporate_owner_name || "—"}</td>
                <td className="p-3 text-slate-700 text-xs">{r.assigned_rep_name || <span className="text-slate-400">Unassigned</span>}</td>
                <td className="p-3 font-mono-plex text-xs">{r.census ?? "—"}/{r.capacity ?? "—"}</td>
                <td className="p-3"><Badge className={STATUS_TONE[r.referral_status]}>{r.referral_status.replace("_", " ")}</Badge></td>
                <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => edit(r)} data-testid={`fac-edit-${r.facility_id}`}>Edit</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {view === "board" && <FacilitiesBoard rows={rows} reload={load} groupBy={boardGroup} reps={reps} health={health} />}

      <ImportFacilitiesDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />
    </div>
  );
}
